import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import WhiteboardImage from '../../../assets/images/whiteboard-static.svg';
import PinkGradientButtonWithArrow from '../../UIControls/PinkGradientButtonWithArrow';

const WhiteboardPanel = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [videoSrc, setVideoSrc] = useState(null);
    const [isFastConnection, setIsFastConnection] = useState(true);

    useEffect(() => {
        if (navigator.connection && navigator.connection.effectiveType) {
            const fastTypes = ['4g', '5g', 'wifi'];
            setIsFastConnection(fastTypes.includes(navigator.connection.effectiveType));
        }
    }, []);

    useEffect(() => {
        if (!isMobile && isFastConnection) {
            import('../../../assets/images/whiteboard-tutorial-V3.mp4').then((module) => {
                setVideoSrc(module.default);
            });
        }
    }, [isMobile, isFastConnection]);

    const handleMainButtonClick = () => {
        navigate('/search');
    };

    return (
        <>
            {/* Title */}
            <Typography
                variant="h5"
                align="center"
                sx={{
                    mb: 2,
                    pt: 5,
                }}
            >
                <Box component="span" fontWeight="bold">
                    NEW
                </Box>{' '}
                Whiteboard
            </Typography>

            {/* Panel */}
            <Box
                component="section"
                maxWidth="xl"
                sx={{
                    mb: 4,
                    width: '100%',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    boxSizing: 'border-box',
                    paddingX: isMobile ? '48px' : '66px',
                    paddingTop: '32px',
                }}
            >
                <Box
                    sx={{
                        backgroundColor: 'white',
                        borderRadius: 2,
                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
                        width: '100%',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        overflow: 'hidden',
                    }}
                >
                    {/* Mobile Layout */}
                    {isMobile ? (
                        <>
                            {/* IMAGE */}
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    p: '20px',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={WhiteboardImage}
                                    alt="Whiteboard Panel Illustration"
                                    sx={{
                                        width: '100%',
                                        maxWidth: '400px',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        margin: '0 auto',
                                    }}
                                />
                            </Box>

                            {/* TEXT */}
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    px: 2,
                                    mb: 3,
                                }}
                            >
                                <Typography
                                    variant="inherit"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 500,
                                        fontSize: '20px',
                                        lineHeight: '1.5',
                                        color: '#000453',
                                    }}
                                >
                                    Enhance pet owner education,
                                    <br />
                                    team collaboration and revision
                                    <br />
                                    skills with our brand-new
                                    <br />
                                    whiteboard feature.
                                </Typography>
                            </Box>

                            {/* BUTTON */}
                            <Box
                                sx={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mt: 3,
                                    mb: 3,
                                    px: 3,
                                }}
                            >
                                <PinkGradientButtonWithArrow
                                    label="Interested? Try it now"
                                    onClick={handleMainButtonClick}
                                    sx={{ fontSize: '14px' }}
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            {/* Desktop Layout */}
                            <Box
                                sx={{
                                    flex: '1',
                                    p: 5,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                }}
                            >
                                <Typography
                                    variant="inherit"
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontStyle: 'normal',
                                        fontWeight: 500,
                                        fontSize: '48px',
                                        lineHeight: '52px',
                                        letterSpacing: '-0.04em',
                                        color: '#000453',
                                        maxWidth: '661px',
                                        mb: 4,
                                    }}
                                >
                                    Enhance pet owner education,
                                    <br />
                                    team collaboration and revision
                                    <br />
                                    skills with our brand-new
                                    <br />
                                    whiteboard feature.
                                </Typography>

                                <PinkGradientButtonWithArrow
                                    label="Interested? Try it now"
                                    onClick={handleMainButtonClick}
                                    sx={{ width: '324px', height: '66px', mt: 2 }}
                                />
                            </Box>
                            {/* IMAGE OR VIDEO (desktop only) */}
                            <Box
                                sx={{
                                    width: '582px',
                                    height: '326px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    mr: 5,
                                }}
                            >
                                {isFastConnection && videoSrc ? (
                                    <Box
                                        component="video"
                                        src={videoSrc}
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        component="img"
                                        src={WhiteboardImage}
                                        alt="Whiteboard Panel Illustration"
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                        }}
                                    />
                                )}
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default WhiteboardPanel;
